import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { persist } from 'zustand/middleware';
import type { GameProject } from '../types/game';

function createEmptyProject(): GameProject {
  return {
    id: `project-${Date.now()}`,
    name: 'Yeni Proje',
    version: '0.1.0',
    combatSystem: {
      damageFormula: { expression: 'base * multiplier', variables: [], description: '' },
      distanceClamp: { min: 0, max: 1000 },
      spreadHeat: { increasePerShot: 0.1, cooldownRate: 0.05, maxHeat: 1.0 },
      piercingSystem: { enabled: false },
      criticalHit: { chance: 0.05, multiplier: 2.0 },
      knockback: { baseForce: 10, formula: 'baseForce * damage / 100' },
      damageTypes: [],
    },
    entities: [],
    weapons: [],
    perks: [],
    powerups: [],
    xpSystem: {
      levelFormula: 'baseXp * (level ^ growthMultiplier)',
      baseXp: 100,
      growthMultiplier: 1.5,
      xpSources: [],
      xpMultipliers: [],
    },
    spawner: {
      initialSpawnRate: 1.0,
      minSpawnRate: 0.3,
      spawnRateDecay: '0.95',
      densityCap: 50,
      densityGrowth: '1.02',
      waves: [],
    },
    gameModes: [],
    turrets: [],
    traps: [],
    craftingRecipes: [],
    companions: [],
    environmentObjects: [],
    metaProgression: {
      currencies: [],
      permanentUpgrades: [],
      unlockables: [],
    },
    lootTables: [],
    uiScreens: [],
  };
}

interface ProjectState {
  project: GameProject;
}

interface ProjectActions {
  setProject: (project: GameProject) => void;
  updateProject: (partial: Partial<GameProject>) => void;
  resetProject: () => void;
  importProject: (jsonString: string) => void;
  setProjectName: (name: string) => void;
}

export const useProjectStore = create<ProjectState & ProjectActions>()(
  persist(
    immer((set) => ({
      project: createEmptyProject(),

      setProject: (project) =>
        set((state) => {
          state.project = project;
        }),

      updateProject: (partial) =>
        set((state) => {
          Object.assign(state.project, partial);
        }),

      resetProject: () =>
        set((state) => {
          state.project = createEmptyProject();
        }),

      importProject: (jsonString) =>
        set((state) => {
          try {
            const parsed = JSON.parse(jsonString) as GameProject;
            state.project = parsed;
          } catch {
            console.error('Proje import edilemedi: Geçersiz JSON');
          }
        }),

      setProjectName: (name) =>
        set((state) => {
          state.project.name = name;
        }),
    })),
    { name: 'bevy-editor-project' }
  )
);
